import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { POSTS } from "@/data/blog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Blog() {
  useEffect(() => {
    document.title = "Care Tips & Senior Care Guides — Providence Partners";
  }, []);

  return (
    <div data-testid="blog-index" className="bg-background">
      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <p className="text-sm uppercase tracking-[0.18em] text-primary">Caregiver tips &amp; senior care guides</p>
        <h1 className="mt-2 max-w-3xl font-serif text-5xl leading-[1.05] text-forest sm:text-6xl">
          Plain-English advice for families seeking care nationwide.
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
          Short, honest guides on adult day care, memory care, respite, and senior placement — written by Providence Partners advisors.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-20 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          {POSTS.map((p) => (
            <Card key={p.slug} data-testid={`blog-card-${p.slug}`} className="flex h-full flex-col rounded-2xl border border-border/60 bg-card p-7 shadow-sm transition-transform hover:-translate-y-1">
              <div className="flex flex-wrap gap-2">
                {p.tags.map((t) => (
                  <Badge key={t} variant="outline" className="rounded-full border-emerald/30 bg-emerald/5 text-emerald text-xs">{t}</Badge>
                ))}
              </div>
              <h2 className="mt-4 font-serif text-2xl leading-snug text-forest">
                <Link to={`/blog/${p.slug}`} className="hover:text-primary">{p.title}</Link>
              </h2>
              <p className="mt-3 flex-1 text-base text-muted-foreground">{p.excerpt}</p>
              <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.readMinutes} min read</span>
                </span>
                <Link to={`/blog/${p.slug}`} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                  Read <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </Card>
          ))}
        </div>

        <Card className="mt-12 rounded-2xl border border-gold/40 bg-gold/5 p-7 text-center shadow-sm">
          <p className="font-serif text-2xl text-forest">Ready to talk to a real person?</p>
          <p className="mt-2 text-muted-foreground">A Providence Partners advisor is on call — same-day callbacks most weekdays.</p>
          <Button asChild className="mt-5 rounded-full bg-primary px-7 py-6 text-base text-primary-foreground" data-testid="blog-call-btn">
            <a href="tel:+15863068449"><Phone className="mr-2 h-4 w-4" /> (586) 306-8449 · Always available 24/7</a>
          </Button>
        </Card>
      </section>
    </div>
  );
}
